import { emitDevHarness } from "@/dev-harness/telemetry";
import type {
  InstallFile,
  InstallFileSegment,
  Lane,
  MessageRecord,
  RuntimeEvent,
} from "@/runtime/events";

import type {
  HeavyOperationFailureDomain,
  HeavyOperationRequest,
  HeavyOperationRequestEnvelope,
  HeavyOperationSuccess,
  HeavyWorkerMessage,
  HeavyWorkerTelemetry,
  InstallFileSegmentSuccess,
  InstallFileSuccess,
  LlmTurnSuccess,
  MicTurnSuccess,
  WorkerCapturedAudio,
} from "@/runtime/heavy-operation-protocol";

export class HeavyOperationError extends Error {
  constructor(
    readonly domain: HeavyOperationFailureDomain,
    message: string,
  ) {
    super(message);
    this.name = "HeavyOperationError";
  }
}

export interface HeavyOperationTask<TResult extends HeavyOperationSuccess> {
  promise: Promise<TResult>;
  abort: () => void;
}

interface PendingTask {
  taskId: string;
  requestId: string;
  startedAt: number;
  summary: Record<string, unknown>;
  onRuntimeEvent?: (event: RuntimeEvent) => void;
  resolve: (result: HeavyOperationSuccess) => void;
  reject: (reason: unknown) => void;
}

interface SingletonState {
  worker: Worker;
  spawnedAt: number;
  spawnGeneration: number;
  firstMessageSeen: boolean;
}

let heavyTaskCounter = 0;
let heavyRequestCounter = 0;
let workerSpawnGeneration = 0;
let singleton: SingletonState | null = null;
const pendingTasks = new Map<string, PendingTask>();

// Worker generation ledger. Every spawn / boot / first-message / terminate is
// captured with timestamps and reasons so the harness can roll up "what
// workers has this page lifetime ever held?" at any moment. Useful for
// correlating iOS jetsam timing with worker churn.
interface WorkerLedgerEntry {
  generation: number;
  event: "spawn" | "boot" | "first_message" | "terminate";
  reason?: string;
  at_perf_ms: number;
  at_wall_ms: number;
  detail?: Record<string, unknown>;
}
const workerLedger: WorkerLedgerEntry[] = [];
const WORKER_LEDGER_MAX = 200;
function appendWorkerLedger(entry: Omit<WorkerLedgerEntry, "at_perf_ms" | "at_wall_ms">): void {
  workerLedger.push({
    ...entry,
    at_perf_ms: Math.round(performance.now()),
    at_wall_ms: Date.now(),
  });
  // Bound memory; older entries are summarized in the rolled snapshot anyway.
  if (workerLedger.length > WORKER_LEDGER_MAX) {
    workerLedger.splice(0, workerLedger.length - WORKER_LEDGER_MAX);
  }
}
export function getWorkerLedgerSnapshot(): {
  current_generation: number;
  alive: boolean;
  total_spawns: number;
  total_terminates: number;
  recent_entries: WorkerLedgerEntry[];
} {
  const totalSpawns = workerLedger.filter((e) => e.event === "spawn").length;
  const totalTerminates = workerLedger.filter((e) => e.event === "terminate").length;
  return {
    current_generation: singleton?.spawnGeneration ?? workerSpawnGeneration,
    alive: singleton !== null,
    total_spawns: totalSpawns,
    total_terminates: totalTerminates,
    // Last 20 entries — full history if it ever matters lives in the
    // harness JSONL via the per-event emit calls anyway.
    recent_entries: workerLedger.slice(-20),
  };
}

function nextTaskId(kind: HeavyOperationRequest["kind"]): string {
  heavyTaskCounter += 1;
  return `hvy-${kind}-${heavyTaskCounter}`;
}

function nextRequestId(): string {
  heavyRequestCounter += 1;
  return `req-${heavyRequestCounter}`;
}

function createAbortError(): DOMException {
  return new DOMException("Operation aborted", "AbortError");
}

function messageFromUnknown(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === "string" ? error : "Heavy worker failed";
}

function summarizeRequest(request: HeavyOperationRequest): Record<string, unknown> {
  switch (request.kind) {
    case "install-lane":
      return {
        kind: request.kind,
        lane: request.manifest.lane,
        manifest_id: request.manifest.id,
        bytes: request.manifest.bytes,
        files: request.manifest.files.length,
      };
    case "install-file":
      return {
        kind: request.kind,
        lane: request.lane,
        manifest_id: request.manifestId,
        file: request.file.path,
        bytes: request.file.bytes,
      };
    case "install-file-segment":
      return {
        kind: request.kind,
        lane: request.lane,
        manifest_id: request.manifestId,
        file: request.filePath,
        segment_index: request.segmentIndex,
        offset: request.segment.offset,
        bytes: request.segment.bytes,
        reset_file: request.resetFile,
      };
    case "run-smoke":
      return {
        kind: request.kind,
        lane: request.manifest.lane,
        manifest_id: request.manifest.id,
      };
    case "mic-turn":
      return {
        kind: request.kind,
        duration_ms: request.audio.durationMs,
        samples: request.audio.mono.length,
        sample_rate: request.audio.sampleRate,
      };
    case "assistant-turn":
    case "llm-turn":
      return {
        kind: request.kind,
        messages: request.messages.length,
      };
    default:
      return {};
  }
}

function getRequestTransferables(request: HeavyOperationRequest): Transferable[] {
  if (request.kind === "mic-turn") {
    return [request.audio.mono.buffer];
  }

  return [];
}

function relayWorkerTelemetry(telemetry: HeavyWorkerTelemetry): void {
  const pending = telemetry.requestId ? pendingTasks.get(telemetry.requestId) : null;
  const elapsedMs = pending
    ? Math.round(performance.now() - pending.startedAt)
    : singleton
      ? Math.round(performance.now() - singleton.spawnedAt)
      : null;

  emitDevHarness({
    level: telemetry.level,
    kind: telemetry.kind ?? "detail",
    event: telemetry.event,
    msg: telemetry.msg,
    data: {
      task_id: pending?.taskId ?? null,
      request_id: telemetry.requestId ?? null,
      source: "worker",
      elapsed_ms: elapsedMs,
      worker_generation: singleton?.spawnGeneration ?? null,
      ...(telemetry.data ?? {}),
    },
  });
}

function failPendingTasks(reason: { event: string; error: Error; data?: Record<string, unknown> }): void {
  if (!pendingTasks.size) {
    return;
  }

  const affected = Array.from(pendingTasks.values());
  pendingTasks.clear();

  for (const task of affected) {
    emitDevHarness({
      level: "error",
      kind: "incident",
      event: reason.event,
      msg: reason.error.message,
      data: {
        task_id: task.taskId,
        request_id: task.requestId,
        elapsed_ms: Math.round(performance.now() - task.startedAt),
        ...task.summary,
        ...(reason.data ?? {}),
      },
    });
    task.reject(reason.error);
  }
}

function handleWorkerMessage(event: MessageEvent<HeavyWorkerMessage>): void {
  const message = event.data;

  if (!message || typeof message !== "object") {
    return;
  }

  if (singleton && !singleton.firstMessageSeen) {
    singleton.firstMessageSeen = true;
    const bootMs = Math.round(performance.now() - singleton.spawnedAt);
    appendWorkerLedger({
      generation: singleton.spawnGeneration,
      event: "first_message",
      detail: { boot_ms: bootMs, message_type: message.type },
    });
    emitDevHarness({
      kind: "transition",
      event: "worker.first_message",
      data: {
        worker_generation: singleton.spawnGeneration,
        boot_ms: bootMs,
        message_type: message.type,
        ledger: getWorkerLedgerSnapshot(),
      },
    });
  }

  if (message.type === "telemetry") {
    relayWorkerTelemetry(message);
    return;
  }

  const pending = pendingTasks.get(message.requestId);

  if (!pending) {
    emitDevHarness({
      level: "warning",
      kind: "incident",
      event: "worker.orphan_message",
      data: {
        request_id: message.requestId,
        message_type: message.type,
        worker_generation: singleton?.spawnGeneration ?? null,
      },
    });
    return;
  }

  if (message.type === "runtime-event") {
    pending.onRuntimeEvent?.(message.event);
    return;
  }

  pendingTasks.delete(message.requestId);
  const elapsedMs = Math.round(performance.now() - pending.startedAt);

  if (message.type === "result") {
    emitDevHarness({
      kind: "summary",
      event: "worker.task_result",
      data: {
        task_id: pending.taskId,
        request_id: pending.requestId,
        result_kind: message.result.kind,
        elapsed_ms: elapsedMs,
        worker_generation: singleton?.spawnGeneration ?? null,
        ...pending.summary,
      },
    });
    pending.resolve(message.result);

    if (message.result.kind === "run-smoke" && pendingTasks.size === 0) {
      destroySingleton("post-smoke-release");
    }
    return;
  }

  emitDevHarness({
    level: "error",
    kind: "incident",
    event: "worker.task_failure",
    msg: message.failure.message,
    data: {
      task_id: pending.taskId,
      request_id: pending.requestId,
      domain: message.failure.domain,
      elapsed_ms: elapsedMs,
      worker_generation: singleton?.spawnGeneration ?? null,
      ...pending.summary,
    },
  });
  pending.reject(new HeavyOperationError(message.failure.domain, message.failure.message));
}

function handleWorkerError(event: ErrorEvent): void {
  const error = new Error(event.message || "Heavy worker crashed");
  const data: Record<string, unknown> = {
    file: event.filename || null,
    line: event.lineno || null,
    column: event.colno || null,
    worker_generation: singleton?.spawnGeneration ?? null,
    pending_count: pendingTasks.size,
  };

  emitDevHarness({
    level: "error",
    kind: "incident",
    event: "worker.crash",
    msg: error.message,
    data,
  });

  failPendingTasks({ event: "worker.task_failure_from_crash", error, data });
  destroySingleton("crash");
}

function handleWorkerMessageError(): void {
  const error = new Error("Heavy worker returned an unreadable message");
  const data: Record<string, unknown> = {
    worker_generation: singleton?.spawnGeneration ?? null,
    pending_count: pendingTasks.size,
  };

  emitDevHarness({
    level: "error",
    kind: "incident",
    event: "worker.messageerror",
    data,
  });

  failPendingTasks({ event: "worker.task_failure_from_messageerror", error, data });
  destroySingleton("messageerror");
}

function destroySingleton(reason: string): void {
  if (!singleton) {
    return;
  }

  const generation = singleton.spawnGeneration;
  const elapsed = Math.round(performance.now() - singleton.spawnedAt);

  singleton.worker.onmessage = null;
  singleton.worker.onerror = null;
  singleton.worker.onmessageerror = null;

  try {
    singleton.worker.terminate();
  } catch {
    // ignore
  }

  singleton = null;

  appendWorkerLedger({ generation, event: "terminate", reason, detail: { elapsed_ms: elapsed } });
  emitDevHarness({
    kind: "transition",
    event: "worker.terminate",
    data: {
      reason,
      worker_generation: generation,
      elapsed_ms: elapsed,
      ledger: getWorkerLedgerSnapshot(),
    },
  });
}

function ensureSingleton(): SingletonState {
  if (singleton) {
    return singleton;
  }

  workerSpawnGeneration += 1;
  const spawnStartedAt = performance.now();

  const worker = new Worker(new URL("./heavy-operation-worker.ts", import.meta.url), {
    type: "module",
  });

  const spawnSyncMs = Math.round(performance.now() - spawnStartedAt);

  singleton = {
    worker,
    spawnedAt: performance.now(),
    spawnGeneration: workerSpawnGeneration,
    firstMessageSeen: false,
  };

  worker.onmessage = handleWorkerMessage;
  worker.onerror = handleWorkerError;
  worker.onmessageerror = handleWorkerMessageError;

  appendWorkerLedger({
    generation: singleton.spawnGeneration,
    event: "spawn",
    detail: { spawn_sync_ms: spawnSyncMs },
  });
  emitDevHarness({
    kind: "transition",
    event: "worker.spawn",
    data: {
      worker_generation: singleton.spawnGeneration,
      spawn_sync_ms: spawnSyncMs,
      ledger: getWorkerLedgerSnapshot(),
    },
  });

  return singleton;
}

function startHeavyOperationTask<TResult extends HeavyOperationSuccess>(
  request: HeavyOperationRequest,
  options: {
    onRuntimeEvent?: (event: RuntimeEvent) => void;
  } = {},
): HeavyOperationTask<TResult> {
  const taskId = nextTaskId(request.kind);
  const requestId = nextRequestId();
  const summary = summarizeRequest(request);

  const state = ensureSingleton();
  const startedAt = performance.now();

  let resolveTask: (result: HeavyOperationSuccess) => void = () => undefined;
  let rejectTask: (reason: unknown) => void = () => undefined;

  const promise = new Promise<TResult>((resolve, reject) => {
    resolveTask = (result) => resolve(result as TResult);
    rejectTask = reject;
  });

  const pending: PendingTask = {
    taskId,
    requestId,
    startedAt,
    summary,
    onRuntimeEvent: options.onRuntimeEvent,
    resolve: resolveTask,
    reject: rejectTask,
  };

  pendingTasks.set(requestId, pending);

  emitDevHarness({
    kind: "transition",
    event: "worker.task_start",
    data: {
      task_id: taskId,
      request_id: requestId,
      worker_generation: state.spawnGeneration,
      ...summary,
    },
  });

  const envelope: HeavyOperationRequestEnvelope = { requestId, request };

  try {
    state.worker.postMessage(envelope, getRequestTransferables(request));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "postMessage failed";
    pendingTasks.delete(requestId);
    emitDevHarness({
      level: "error",
      kind: "incident",
      event: "worker.task_post_failed",
      msg: errorMessage,
      data: {
        task_id: taskId,
        request_id: requestId,
        worker_generation: state.spawnGeneration,
        ...summary,
      },
    });
    rejectTask(error instanceof Error ? error : new Error(errorMessage));
  }

  return {
    promise,
    abort: () => {
      const removed = pendingTasks.delete(requestId);

      if (!removed) {
        return;
      }

      emitDevHarness({
        kind: "transition",
        event: "worker.task_abort",
        data: {
          task_id: taskId,
          request_id: requestId,
          elapsed_ms: Math.round(performance.now() - startedAt),
          worker_generation: state.spawnGeneration,
          ...summary,
        },
      });
      rejectTask(createAbortError());
    },
  };
}

export async function installFileInWorker(
  lane: Lane,
  manifestId: string,
  file: InstallFile,
  options: {
    onRuntimeEvent?: (event: RuntimeEvent) => void;
  } = {},
): Promise<InstallFileSuccess> {
  const task = startHeavyOperationTask<InstallFileSuccess>(
    {
      kind: "install-file",
      lane,
      manifestId,
      file,
    },
    options,
  );
  return task.promise;
}

// Per-segment install deadline. iOS Safari can put a worker into a
// non-responsive state after a streak of OPFS UnknownErrors (observed
// 2026-04-26: worker.heartbeat stops, neither setTimeout nor setInterval
// callbacks fire in that worker for ~10+ s, no error event surfaces, the
// outer install promise hangs forever). Without this deadline, a single
// hung segment task would leave the install lane in a "still installing"
// limbo with no way to recover except a manual reload. With this
// deadline, after `INSTALL_SEGMENT_DEADLINE_MS` we forcibly terminate
// the worker, surface a clean failure to the orchestrator (which will
// either restart the file from segment 0 or fail the lane), and the
// next segment task spawns into a fresh worker. Per consultant
// 2026-04-27: this is "table stakes" — it doesn't fix install, it just
// keeps the UI from getting wedged.
const INSTALL_SEGMENT_DEADLINE_MS = 25_000;

export async function installFileSegmentInWorker(
  lane: Lane,
  manifestId: string,
  filePath: string,
  segment: InstallFileSegment,
  segmentIndex: number,
  resetFile: boolean,
  isFirstSegment: boolean,
  isLastSegment: boolean,
  options: {
    onRuntimeEvent?: (event: RuntimeEvent) => void;
  } = {},
): Promise<InstallFileSegmentSuccess> {
  const task = startHeavyOperationTask<InstallFileSegmentSuccess>(
    {
      kind: "install-file-segment",
      lane,
      manifestId,
      filePath,
      segment,
      segmentIndex,
      resetFile,
      isFirstSegment,
      isLastSegment,
    },
    options,
  );
  // Race the task promise against a deadline. On deadline: terminate the
  // worker (next operation will spawn a fresh one), emit a loud telemetry
  // event so the harness shows the wedge, and reject the promise so the
  // orchestrator surfaces a clean failure instead of hanging forever.
  let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await new Promise<InstallFileSegmentSuccess>((resolve, reject) => {
      deadlineTimer = setTimeout(() => {
        const lastLedger = getWorkerLedgerSnapshot();
        emitDevHarness({
          level: "error",
          kind: "incident",
          event: "worker.request_deadline",
          msg: "Install segment worker deadline exceeded — terminating worker",
          data: {
            kind: "install-file-segment",
            lane,
            manifest_id: manifestId,
            file: filePath,
            segment_index: segmentIndex,
            offset: segment.offset,
            bytes: segment.bytes,
            deadline_ms: INSTALL_SEGMENT_DEADLINE_MS,
            ledger: lastLedger,
          },
        });
        terminateHeavyWorker("install-segment-deadline");
        reject(
          new HeavyOperationError(
            "install",
            `install-file-segment deadline ${INSTALL_SEGMENT_DEADLINE_MS}ms exceeded for ${filePath} segment ${segmentIndex}`,
          ),
        );
      }, INSTALL_SEGMENT_DEADLINE_MS);
      task.promise.then(resolve, reject);
    });
  } finally {
    if (deadlineTimer !== null) clearTimeout(deadlineTimer);
  }
}

export async function runLaneSmokeCheckInWorker(
  manifest: import("@/runtime/events").LaneManifest,
  options: {
    onRuntimeEvent?: (event: RuntimeEvent) => void;
  } = {},
): Promise<import("@/runtime/events").SmokeCheckRecord> {
  const task = startHeavyOperationTask<import("./heavy-operation-protocol").RunSmokeSuccess>(
    {
      kind: "run-smoke",
      manifest,
    },
    options,
  );
  const result = await task.promise;
  return result.smoke;
}

/**
 * Force the singleton worker to terminate. Next operation will spawn a fresh worker.
 * Used by the install orchestrator between segments of large segmented files so each
 * segment download runs in its own worker process — the whole motivation for the
 * segmented installer is to ensure no single browser task, worker lifetime, fetch
 * body, or OPFS handle ever owns a multi-megabyte stretch of work.
 */
export function terminateHeavyWorker(reason: string): void {
  if (!singleton) {
    return;
  }
  destroySingleton(reason);
}

export function startMicTurnTask(
  audio: WorkerCapturedAudio,
  options: {
    onRuntimeEvent?: (event: RuntimeEvent) => void;
  } = {},
): HeavyOperationTask<MicTurnSuccess> {
  return startHeavyOperationTask<MicTurnSuccess>(
    {
      kind: "mic-turn",
      audio,
    },
    options,
  );
}

export function startLlmTurnTask(
  messages: MessageRecord[],
  options: {
    onRuntimeEvent?: (event: RuntimeEvent) => void;
  } = {},
): HeavyOperationTask<LlmTurnSuccess> {
  return startHeavyOperationTask<LlmTurnSuccess>(
    {
      kind: "llm-turn",
      messages,
    },
    options,
  );
}

export function describeHeavyWorkerError(error: unknown): string {
  return messageFromUnknown(error);
}
